/**
 * A MEZŐJAVÍTÁS HORGA (#42) — a nyitástól a mentésig.
 *
 * ═══ MIÉRT NEM OPTIMISTA, A LEFŰZÉSSEL ELLENTÉTBEN ═══
 *
 * A 2.6/22 a lefűzésre mondta ki az optimista mentést: az `Enter` továbblép,
 * a mentés a háttérben megy. Ott ez helyes, mert a művelet EGY BITET állít, és
 * a kliens előre ki tudja számolni, hogy a szerver elfogadná-e (`blockFor`).
 *
 * A javítás más. Amit a könyvelő beírt, azt a szerver ÉRTELMEZI — az `1.234`
 * ezerkétszázharmincnégy, a `2026-02-31` pedig visszautasítás —, és ezt a
 * felület szándékosan nem ismétli meg (`fieldCorrection.ts`). Optimista
 * mentésnél tehát a sor visszajönne egy olyan hibával, amit a könyvelő már
 * nem lát a szeme előtt: becsukta az űrlapot, továbblépett, és két sorral
 * később derül ki, hogy a dátum nem létezik. A javítás ritka művelet — a
 * megvárás ára itt kisebb, mint a visszagörgetésé.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { DocType } from '@/i18n/generated';
import { ApiError, api } from '@/lib/api-client';
import { typeOptionsFor } from './docTypeChoice';
import {
  type CorrectableField,
  changesOf,
  type Draft,
  draftFrom,
  type FieldsResponse,
  type FieldView,
  inDisplayOrder,
} from './fieldCorrection';

export interface FieldCorrectionState {
  /** Nyitva van-e az űrlap. */
  open: boolean;
  /** `null`, amíg a mezők be nem töltöttek. */
  fields: FieldView[] | null;
  draft: Draft | null;
  /**
   * A TÍPUS PISZKOZATA (#92). `null`, ha a horog típus nélkül fut (a hívó
   * nem adott típust) — akkor a választó meg sem jelenik.
   */
  docType: DocType | null;
  /** Amire javítani lehet (#92) — a cég engedélye + a jelenlegi típus. */
  typeOptions: DocType[];
  /** Hány mező tér el a tárolttól — a lábléc száma. A típus is egynek számít. */
  changedCount: number;
  saving: boolean;
  /** A szerver hibája, a mező nevével, ha megmondta. */
  error: { message: string; fieldKey: CorrectableField | null } | null;
  /** Betöltés vagy állapotváltás miatt nem szerkeszthető. */
  loading: boolean;
  /**
   * Az űrlap megnyitása. A `preset` a „Típus?" jelzés javaslata (#93): az
   * űrlap azzal a típussal nyílik, de NEM ment — a `Mentem` a könyvelőé.
   */
  start: (preset?: DocType) => void;
  cancel: () => void;
  change: (fieldKey: CorrectableField, value: string) => void;
  changeType: (docType: DocType) => void;
  /** A javítás visszavonása: a mező visszaesik a gépi értékre. */
  revert: (fieldKey: CorrectableField, machineValue: string) => void;
  save: () => void;
}

export function useFieldCorrection(
  documentId: string | null,
  onSaved?: () => void,
  storedDocType: DocType | null = null
): FieldCorrectionState {
  const [open, setOpen] = useState(false);
  const [draftType, setDraftType] = useState<DocType | null>(null);
  const [allowedTypes, setAllowedTypes] = useState<DocType[]>([]);
  /**
   * ⚠️ A RÉSZBEN SIKERÜLT MENTÉS NYOMA. A típus és a mezők két kérés: ha a
   * típus átment, de a mezők nem, a lista még a RÉGI típust hordja (nem
   * töltjük újra, hogy az űrlap a begépelt szöveggel nyitva maradjon). Ez a
   * mentett típus a következő próbánál az összevetés alapja — enélkül a
   * típus kétszer menne el, és kétszer kerülne a naplóba.
   */
  const [savedType, setSavedType] = useState<DocType | null>(null);
  const currentType = savedType ?? storedDocType;

  /**
   * A FÉLBEN MARADT MENTÉS A SORVÁLTÁST IS TÚLÉLI. A sorváltás effektje csak a
   * `documentId`-t látja; hogy volt-e a szerveren már átment típus, azt ez a
   * ref mondja meg — és az elhagyott sornak újra kell töltődnie, különben a
   * kupacban a régi típussal, „javítva" nélkül állna.
   */
  const savedTypeRef = useRef<DocType | null>(null);
  useEffect(() => {
    savedTypeRef.current = savedType;
  });

  /** Melyik bizonylaton fut MOST az űrlap — a későn érkező válasz ehhez mér. */
  const activeDocument = useRef(documentId);
  const [fields, setFields] = useState<FieldView[] | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<FieldCorrectionState['error']>(null);

  /**
   * ⚠️ A VISSZAHÍVÁS EFFEKTBEN FRISSÜL, NEM A RENDERBEN. Egy renderfázisú
   * ref-írás működik, de a React nem garantálja: egy eldobott render is
   * mutálná. Ugyanaz a minta, mint a `useNavSync`-ben. *(#37 code review.)*
   */
  const saved = useRef(onSaved);
  useEffect(() => {
    saved.current = onSaved;
  });

  /**
   * ⚠️ A SORVÁLTÁS BECSUKJA AZ ŰRLAPOT. Enélkül a könyvelő a MÁSIK bizonylat
   * fejlécében látná a saját, félbehagyott javítását — és elmentené rá.
   *
   * A `documentId` a törzsben is szerepel (#92: az `activeDocument`), tehát a
   * Biome már nem tartja fölöslegesnek — a korábbi `biome-ignore` ezzel
   * elesett. A függőség eltávolítását a `Worklist.spec.tsx` „a SORVÁLTÁS
   * becsukja az űrlapot" tesztje továbbra is megfogja.
   */
  useEffect(() => {
    activeDocument.current = documentId;
    if (savedTypeRef.current !== null) {
      savedTypeRef.current = null;
      saved.current?.();
    }
    setOpen(false);
    setFields(null);
    setDraft(null);
    setDraftType(null);
    setSavedType(null);
    setError(null);
  }, [documentId]);

  const start = useCallback(
    (preset?: DocType) => {
      if (documentId === null) {
        return;
      }

      setOpen(true);
      setError(null);
      setLoading(true);
      // ⚠️ A JAVASLAT CSAK A PISZKOZATBA MEGY (#93). A tárolt típus (`currentType`)
      // marad az összevetés alapja — így a lábléc „1 mező változott"-ot mutat,
      // és az `Esc` mindent visszavon. Típusválasztó nélküli bizonylaton
      // (`storedDocType === null`) a javaslat nem ül be.
      setDraftType(preset !== undefined && currentType !== null ? preset : currentType);

      let stale = false;

      api<FieldsResponse>(`/documents/${documentId}/fields`)
        .then((response) => {
          if (stale) {
            return;
          }
          // ⚠️ A NEM SZERKESZTHETŐ BIZONYLATON BE SEM NYITUNK. A tiltás a
          // szerveren van (409), ez csak annyit tesz, hogy nem kínálunk fel egy
          // űrlapot, ami úgyis visszapattanna.
          if (!response.editable) {
            setOpen(false);
            setError({ message: 'A bizonylatot már elintézték.', fieldKey: null });
            return;
          }
          setAllowedTypes(response.docTypes ?? []);
          const ordered = inDisplayOrder(response.fields);
          setFields(ordered);
          setDraft(draftFrom(ordered));
        })
        .catch((cause: unknown) => {
          if (stale) {
            return;
          }
          setOpen(false);
          setError({ message: messageOf(cause), fieldKey: null });
        })
        .finally(() => {
          if (!stale) {
            setLoading(false);
          }
        });

      return () => {
        stale = true;
      };
    },
    [documentId, currentType]
  );

  const cancel = useCallback(() => {
    setOpen(false);
    setFields(null);
    setDraft(null);
    setDraftType(null);
    setError(null);
    // A FÉLBEN MARADT MENTÉS TÍPUSA MÁR A SZERVEREN VAN — a lista csak most
    // tölt újra, amikor az űrlapon már nincs mit elveszíteni.
    if (savedType !== null) {
      setSavedType(null);
      saved.current?.();
    }
  }, [savedType]);

  const change = useCallback((fieldKey: CorrectableField, value: string) => {
    setDraft((current) => (current === null ? current : { ...current, [fieldKey]: value }));
    // A HIBA A LEÜTÉSRE TŰNIK EL. Egy kijavított mező mellett álló piros
    // üzenet azt állítaná, hogy még mindig rossz — a könyvelő pedig már
    // átírta.
    setError((current) => (current?.fieldKey === fieldKey ? null : current));
  }, []);

  const changeType = useCallback((docType: DocType) => {
    setDraftType(docType);
    setError((current) => (current?.fieldKey === null ? null : current));
  }, []);

  const revert = useCallback((fieldKey: CorrectableField, machineValue: string) => {
    // A VISSZAÁLLÍTÁS A MEZŐBE ÍRJA A GÉPI ÉRTÉKET. Innentől a `changesOf`
    // látja, hogy ez egyezik a tárolt `ocrValue`-val, de NEM az
    // `effectiveValue`-val — tehát változásként megy el, `null` értékkel a
    // szerver felé csak akkor, ha a könyvelő tényleg kiüríti.
    setDraft((current) => (current === null ? current : { ...current, [fieldKey]: machineValue }));
    setError((current) => (current?.fieldKey === fieldKey ? null : current));
  }, []);

  const save = useCallback(() => {
    if (documentId === null || fields === null || draft === null || saving) {
      return;
    }

    const changes = changesOf(fields, draft);
    const typeChanged = draftType !== null && draftType !== currentType;

    // NINCS VÁLTOZÁS: becsukjuk, és nem hívunk. Egy üres köteget a szerver
    // `400`-zal utasítana vissza, és a könyvelő hibaüzenetet kapna azért,
    // mert meggondolta magát.
    if (changes.length === 0 && !typeChanged) {
      cancel();
      return;
    }

    setSaving(true);
    setError(null);

    // ⚠️ ELŐBB A TÍPUS, UTÁNA A MEZŐK. A típus az irányt is viszi, és az
    // irány dönti el, mi a minimum (#51) — a mezők mentése így már az új
    // irány szerint számolja a `processingState`-et. Fordított sorrendben a
    // mezőmentés még a régi irány minimumán mérne.
    const typeSave = typeChanged
      ? api(`/documents/${documentId}/type-correction`, {
          method: 'PATCH',
          body: JSON.stringify({ docType: draftType }),
        }).then(() => {
          // ⚠️ KÉSŐN ÉRKEZŐ VÁLASZ: ha közben sort váltott, a típus a MÁSIK
          // bizonylaté — az itteni alapvonalat nem szabad elmozdítania. A lista
          // viszont tölts újra, mert a szerveren már az új típus áll.
          if (activeDocument.current === documentId) {
            setSavedType(draftType);
          } else {
            saved.current?.();
          }
        })
      : Promise.resolve();
    const stillHere = () => activeDocument.current === documentId;

    typeSave
      .then(() =>
        changes.length === 0
          ? undefined
          : api(`/documents/${documentId}/fields`, {
              method: 'PATCH',
              body: JSON.stringify({ fields: changes }),
            })
      )
      .then(() => {
        if (stillHere()) {
          setOpen(false);
          setFields(null);
          setDraft(null);
          setDraftType(null);
          setSavedType(null);
        }
        saved.current?.();
      })
      .catch((cause: unknown) => {
        if (!stillHere()) {
          return;
        }
        // ⚠️ AZ ŰRLAP NYITVA MARAD. A begépelt szöveg a könyvelőé; ha
        // becsuknánk, a javítása elveszne egy hibaüzenettel együtt.
        setError({ message: messageOf(cause), fieldKey: fieldKeyOf(cause) });
      })
      .finally(() => {
        setSaving(false);
      });
  }, [cancel, currentType, documentId, draft, draftType, fields, saving]);

  const typeDirty = draftType !== null && draftType !== currentType;
  const changedCount =
    fields === null || draft === null ? 0 : changesOf(fields, draft).length + (typeDirty ? 1 : 0);

  return {
    open,
    fields,
    draft,
    docType: storedDocType === null ? null : draftType,
    typeOptions: currentType === null ? [] : typeOptionsFor(allowedTypes, currentType),
    changedCount,
    saving,
    error,
    loading,
    start,
    cancel,
    change,
    changeType,
    revert,
    save,
  };
}

/** A szerver magyar üzenete; ha nincs, egy általános mondat. */
function messageOf(cause: unknown): string {
  if (cause instanceof ApiError && cause.message !== '') {
    return cause.message;
  }
  return 'A mentés nem sikerült.';
}

/** Melyik mezőt jelölje meg a felület — a szerver mondja meg (`400`). */
function fieldKeyOf(cause: unknown): CorrectableField | null {
  if (cause instanceof ApiError && cause.fieldKey !== undefined) {
    return cause.fieldKey as CorrectableField;
  }
  return null;
}
